import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { ReactComponent as SearchIcon } from 'assets/images/search.svg';
import { ReactComponent as CloseIcon } from 'assets/images/close.svg';

const useStyles = makeStyles({
  root: {
    background: 'white',
    height: 50,
    borderRadius: 4,
    border: '1px solid #888EFF'
  },
  input: {
    width: 355,
    height: 37,
    padding: '6px 20px 6px 4px !important'
  },
  option: {
    width: 355,
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '17px',
    paddingLeft: '18px',
    paddingRight: '25px',
    '& >img': {
      borderRadius: '50%',
      marginRight: '22px'
    },
    borderBottom: '1px solid rgba(46, 52, 82, 0.05)'
  },
  endAdornment: {
    marginTop: '4px',
    marginRight: '20px'
  },
  paper: {
    border: '1px solid #888EFF',
    marginTop: '-2px',
    borderRadius: '0px 0px 4px 4px'
  },
  listbox: {
    padding: 0
  }
});

const AutoComplete = ({
  options: _options,
  placeholder,
  onSelect,
  filterOptions,
  renderOption,
  value: _value,
  resetSearchOnSelect,
  style,
  getOptionLabel,
  useAsSelect,
  initialValue,
  disabled,
  className
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(useAsSelect ? initialValue : '');
  useEffect(() => {
    if (_value === null) {
      setSearch('');
    }
  }, [_value]);

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      classes={{
        root: classes.root,
        option: classes.option,
        input: classes.input,
        endAdornment: classes.endAdornment,
        paper: classes.paper,
        listbox: classes.listbox
      }}
      onClose={() => {
        if (resetSearchOnSelect) {
          setSearch('');
        }
        setOpen(false);
      }}
      closeIcon={<CloseIcon />}
      options={_options}
      filterOptions={(options, { inputValue }) => filterOptions(options, inputValue)}
      inputValue={search}
      value={_value}
      onChange={(e, value) => {
        if (typeof value === 'object') {
          onSelect(value);
          setSearch(value?.name || '');
          setOpen(false);
        }
      }}
      autoHighlight
      style={style}
      className={className}
      open={open}
      getOptionLabel={(option) => {
        if (typeof option === 'object') {
          return getOptionLabel(option);
        }
        return '';
      }}
      renderOption={(option) => renderOption(option)}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            startAdornment: <SearchIcon style={{ marginLeft: '16px', marginRight: '4px' }} />,
            value: search,
            placeholder,
            disableUnderline: true,
            onChange: ({ target: { value } }) => {
              setSearch(value);
              if (value) {
                setOpen(true);
              } else {
                setOpen(false);
              }
            }
          }}
        />
      )}
    />
  );
};

AutoComplete.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string.isRequired,
      photo: PropTypes.string
    })
  ).isRequired,
  placeholder: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  filterOptions: PropTypes.func.isRequired,
  renderOption: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      value: PropTypes.number
    })
  ]),
  resetSearchOnSelect: PropTypes.bool.isRequired,
  style: PropTypes.shape({}),
  className: PropTypes.shape({}),
  getOptionLabel: PropTypes.func.isRequired,
  useAsSelect: PropTypes.bool,
  initialValue: PropTypes.string,
  disabled: PropTypes.bool
};

AutoComplete.defaultProps = {
  value: null,
  useAsSelect: false,
  style: {},
  initialValue: '',
  disabled: false,
  className: {}
};

export default AutoComplete;
